import { https } from "../http";
import store from "../store";
import {
  setLoading,
  setDashboard,
  setAlerts,
  setAlertsCount,
  setStaffOverview,
  setStaffOverviewCount,
  setFacilityOverview,
  setFacilityOverviewCount,
  setClientOverview,
  setClientOverviewCount,
  setCredentialTypes,
  setClientItems,
  setFacilityItems,
  setStaffCredentials,
  setClientRecords,
  setFacilityRecords,
  setSettings,
  setAddLoading,
  setClientDocOverview,
  setClientDocOverviewCount,
  setDocumentTemplates,
  setOperationalTasks,
  setOperationalTasksCount,
  setOperationalTasksLoading,
  patchClientItem,
  patchFacilityItem,
} from "../store/reducers/compliance";
import { showError } from "../utils/index";
import swal from "sweetalert";

// ── Dashboard ──
const getComplianceDashboard = async (facilityId) => {
  try {
    store.dispatch(setLoading(true));
    const query = facilityId ? `?facilityId=${facilityId}` : "";
    const response = await https().get(`/compliance/dashboard${query}`);
    if (response?.status === 200) {
      store.dispatch(setDashboard(response?.data));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// ── Alerts ──
const getComplianceAlerts = async (page = 1, limit = 20) => {
  try {
    const response = await https().get(`/compliance/alerts?page=${page}&limit=${limit}`);
    if (response?.status === 200) {
      store.dispatch(setAlerts(response?.data?.alerts));
      store.dispatch(setAlertsCount(response?.data?.total));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// ── Credential Types ──
const getCredentialTypes = async (page = "all") => {
  try {
    const response = await https().get(`/compliance/credential-types/${page}`);
    if (response?.status === 200) {
      store.dispatch(setCredentialTypes(response?.data?.credentialTypes));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const addCredentialType = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/credential-types", data);
    if (response?.status === 200) {
      swal({ title: "Credential type created", icon: "success" });
      getCredentialTypes();
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const updateCredentialType = async (id, data) => {
  try {
    const response = await https().put(`/compliance/credential-types/${id}`, data);
    if (response?.status === 200) {
      swal({ title: "Updated", icon: "success" });
      getCredentialTypes();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteCredentialType = async (id) => {
  try {
    const response = await https().delete(`/compliance/credential-types/${id}`);
    if (response?.status === 200) {
      swal({ title: "Deleted", icon: "success" });
      getCredentialTypes();
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

// ── Staff Credentials ──
const getStaffCredentials = async (staffId) => {
  try {
    const response = await https().get(`/compliance/staff-credentials/${staffId}`);
    if (response?.status === 200) {
      store.dispatch(setStaffCredentials(response?.data?.credentials));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getStaffCredentialsOverview = async (page = 1, search = "", status = "") => {
  try {
    store.dispatch(setLoading(true));
    const response = await https().get(
      `/compliance/staff-credentials-overview/${page}?search=${search}&status=${status}`
    );
    if (response?.status === 200) {
      store.dispatch(setStaffOverview(response?.data?.staff));
      store.dispatch(setStaffOverviewCount(response?.data?.count));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const saveStaffCredential = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/staff-credentials", data);
    if (response?.status === 200) {
      swal({ title: "Credential saved", icon: "success" });
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const verifyStaffCredential = async (id) => {
  try {
    const response = await https().put(`/compliance/staff-credentials/${id}/verify`);
    if (response?.status === 200) {
      swal({ title: "Credential verified", icon: "success" });
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteStaffCredential = async (id) => {
  try {
    const response = await https().delete(`/compliance/staff-credentials/${id}`);
    if (response?.status === 200) {
      swal({ title: "Deleted", icon: "success" });
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

// ── Client Compliance Items ──
const getClientComplianceItems = async (page = "all", search = "") => {
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await https().get(`/compliance/client-items/${page}${query}`);
    if (response?.status === 200) {
      store.dispatch(setClientItems(response?.data?.items));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const addClientComplianceItem = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/client-items", data);
    if (response?.status === 200) {
      swal({ title: "Compliance item created", icon: "success" });
      getClientComplianceItems();
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const updateClientComplianceItem = async (id, data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().put(`/compliance/client-items/${id}`, data);
    if (response?.status === 200) {
      swal({ title: "Updated", icon: "success" });
      getClientComplianceItems();
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteClientComplianceItem = async (id) => {
  try {
    const response = await https().delete(`/compliance/client-items/${id}`);
    if (response?.status === 200) {
      swal({ title: "Deleted", icon: "success" });
      getClientComplianceItems();
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

// ── Client Compliance Records ──
const getClientComplianceRecords = async (clientId) => {
  try {
    const response = await https().get(`/compliance/client-records/${clientId}`);
    if (response?.status === 200) {
      store.dispatch(setClientRecords(response?.data?.records));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getClientComplianceOverview = async (page = 1, facilityId = "") => {
  try {
    store.dispatch(setLoading(true));
    const query = facilityId ? `?facilityId=${facilityId}` : "";
    const response = await https().get(`/compliance/client-overview/${page}${query}`);
    if (response?.status === 200) {
      store.dispatch(setClientOverview(response?.data?.clients));
      store.dispatch(setClientOverviewCount(response?.data?.count));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const saveClientComplianceRecord = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/client-records", data);
    if (response?.status === 200) {
      swal({ title: "Record saved", icon: "success" });
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

// ── Facility Documents Overview ──
const getFacilityDocumentsOverview = async (page = 1, search = "") => {
  try {
    store.dispatch(setLoading(true));
    const response = await https().get(
      `/compliance/facility-documents-overview/${page}?search=${encodeURIComponent(search)}`
    );
    if (response?.status === 200) {
      store.dispatch(setFacilityOverview(response?.data?.facilities));
      store.dispatch(setFacilityOverviewCount(response?.data?.count));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// ── Client Documents Overview ──
const getClientDocumentsOverview = async (page = 1, search = "") => {
  try {
    store.dispatch(setLoading(true));
    const response = await https().get(
      `/compliance/client-documents-overview/${page}?search=${encodeURIComponent(search)}`
    );
    if (response?.status === 200) {
      store.dispatch(setClientDocOverview(response?.data?.clients));
      store.dispatch(setClientDocOverviewCount(response?.data?.count));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// ── Facility Compliance Items ──
const getFacilityComplianceItems = async (page = "all", search = "") => {
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await https().get(`/compliance/facility-items/${page}${query}`);
    if (response?.status === 200) {
      store.dispatch(setFacilityItems(response?.data?.items));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const addFacilityComplianceItem = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/facility-items", data);
    if (response?.status === 200) {
      swal({ title: "Requirement created", icon: "success" });
      getFacilityComplianceItems();
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const updateFacilityComplianceItem = async (id, data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().put(`/compliance/facility-items/${id}`, data);
    if (response?.status === 200) {
      swal({ title: "Requirement updated", icon: "success" });
      getFacilityComplianceItems();
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteFacilityComplianceItem = async (id) => {
  try {
    const response = await https().delete(`/compliance/facility-items/${id}`);
    if (response?.status === 200) {
      swal({ title: "Deleted", icon: "success" });
      getFacilityComplianceItems();
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

// ── Facility Compliance Records ──
const getFacilityComplianceRecords = async (facilityId) => {
  try {
    const response = await https().get(`/compliance/facility-records/${facilityId}`);
    if (response?.status === 200) {
      store.dispatch(setFacilityRecords(response?.data?.records));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const saveFacilityComplianceRecord = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const response = await https().post("/compliance/facility-records", data);
    if (response?.status === 200) {
      swal({ title: "Record saved", icon: "success" });
      store.dispatch(setAddLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error?.response?.data?.message);
    return false;
  }
};

// ── Audit ──
const runComplianceAudit = async (type, days = 30, from = null, to = null) => {
  try {
    let url = `/compliance/audit/${type}?days=${days}`;
    if (from && to) url = `/compliance/audit/${type}?from=${from}&to=${to}`;
    const response = await https().get(url);
    if (response?.status === 200) {
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

// ── Settings ──
const getComplianceSettings = async () => {
  try {
    const response = await https().get("/compliance/settings");
    if (response?.status === 200) {
      store.dispatch(setSettings(response?.data?.settings));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateComplianceSettings = async (data) => {
  try {
    const response = await https().put("/compliance/settings", data);
    if (response?.status === 200) {
      swal({ title: "Settings updated", icon: "success" });
      store.dispatch(setSettings(response?.data?.settings));
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

// ── Document Templates ──
const getDocumentTemplates = async () => {
  try {
    const response = await https().get("/compliance/document-templates");
    if (response?.status === 200) {
      store.dispatch(setDocumentTemplates(response?.data));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const toggleDocumentTemplate = async (category, folder, templateName, active) => {
  try {
    const response = await https().put("/compliance/document-templates/toggle", {
      category,
      folder,
      templateName,
      active,
    });
    if (response?.status === 200) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const toggleDocumentFolder = async (category, folder, active) => {
  try {
    const response = await https().put("/compliance/document-templates/toggle-folder", {
      category,
      folder,
      active,
    });
    if (response?.status === 200) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const createCustomFolder = async (category, folder) => {
  try {
    const response = await https().post("/compliance/document-templates/custom-folder", {
      category,
      folder,
    });
    if (response?.status === 201) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const addCustomItem = async (category, folder, templateName) => {
  try {
    const response = await https().post("/compliance/document-templates/custom-item", {
      category,
      folder,
      templateName,
    });
    if (response?.status === 201) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteCustomFolder = async (category, folder) => {
  try {
    const response = await https().delete("/compliance/document-templates/custom-folder", {
      data: { category, folder },
    });
    if (response?.status === 200) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

const deleteCustomItem = async (category, folder, templateName) => {
  try {
    const response = await https().delete("/compliance/document-templates/custom-item", {
      data: { category, folder, templateName },
    });
    if (response?.status === 200) {
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return false;
  }
};

// ── Operational Tasks ──
const getOperationalTasks = async (page = 1, limit = 10, status = "all", search = "") => {
  try {
    store.dispatch(setOperationalTasksLoading(true));
    const params = new URLSearchParams({ page, limit });
    if (status && status !== "all") params.append("status", status);
    if (search) params.append("search", search);
    const res = await https().get(`/compliance/operational-tasks?${params.toString()}`);
    store.dispatch(setOperationalTasks(res.data.tasks));
    store.dispatch(setOperationalTasksCount(res.data.count));
    store.dispatch(setOperationalTasksLoading(false));
    return res.data;
  } catch (error) {
    store.dispatch(setOperationalTasksLoading(false));
    showError(error);
    return false;
  }
};

const createOperationalTask = async (data) => {
  try {
    store.dispatch(setAddLoading(true));
    const res = await https().post("/compliance/operational-tasks", data);
    store.dispatch(setAddLoading(false));
    swal("Success", "Task created successfully", "success");
    return res.data;
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error);
    return false;
  }
};

const updateOperationalTask = async (id, data) => {
  try {
    store.dispatch(setAddLoading(true));
    const res = await https().put(`/compliance/operational-tasks/${id}`, data);
    store.dispatch(setAddLoading(false));
    swal("Success", "Task updated successfully", "success");
    return res.data;
  } catch (error) {
    store.dispatch(setAddLoading(false));
    showError(error);
    return false;
  }
};

const deleteOperationalTask = async (id) => {
  try {
    await https().delete(`/compliance/operational-tasks/${id}`);
    swal("Success", "Task deleted successfully", "success");
    return true;
  } catch (error) {
    showError(error);
    return false;
  }
};

const updateOperationalTaskStatus = async (id, status) => {
  try {
    const res = await https().put(`/compliance/operational-tasks/${id}/status`, { status });
    return res.data;
  } catch (error) {
    showError(error);
    return false;
  }
};

const getMyComplianceTasks = async (page = 1, limit = 20, status = "all") => {
  try {
    const params = new URLSearchParams({ page, limit });
    if (status && status !== "all") params.append("status", status);
    const res = await https().get(`/compliance/my-tasks?${params.toString()}`);
    return res.data;
  } catch (error) {
    showError(error);
    return null;
  }
};

// ── Overdue Staff Tasks (dashboard overview panel) ──
const getOverdueStaffTasks = async () => {
  try {
    const res = await https().get("/compliance/overdue-tasks");
    return res?.data || { tasks: [], count: 0 };
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return { tasks: [], count: 0 };
  }
};

// ── Task Instances (per-day records for recurring tasks) ──
const getTaskInstances = async (page = 1, limit = 20, startDate, endDate, status = "all", staffId = "") => {
  try {
    const params = new URLSearchParams({ page, limit });
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (status && status !== "all") params.append("status", status);
    if (staffId) params.append("staffId", staffId);
    const res = await https().get(`/compliance/operational-task-instances?${params.toString()}`);
    return res?.data || { instances: [], count: 0 };
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return { instances: [], count: 0 };
  }
};

const updateTaskInstanceStatus = async (instanceId, status) => {
  try {
    const res = await https().put(`/compliance/operational-task-instances/${instanceId}/status`, { status });
    return res?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

const triggerInstanceGeneration = async () => {
  try {
    const res = await https().post("/compliance/generate-task-instances");
    return res?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

const getWeeklyTaskBoard = async (weekOffset = 0) => {
  try {
    const res = await https().get(`/compliance/weekly-task-board?weekOffset=${weekOffset}`);
    return res?.data || { days: [], summary: {}, staffBreakdown: [] };
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return { days: [], summary: {}, staffBreakdown: [] };
  }
};

const updateTaskInstanceStaffStatus = async (instanceId, status, staffId) => {
  try {
    const res = await https().put(`/compliance/operational-task-instances/${instanceId}/status`, { status, staffId });
    return res?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

// ── SkAi Beam ──
const getStateRequirements = async () => {
  try {
    const res = await https().get("/compliance/state-requirements");
    return res?.data?.data || { requirements: [], alerts: [] };
  } catch (error) {
    return { requirements: [], alerts: [] };
  }
};

// Conversation history now lives server-side (OpenAI Conversations API), so we no
// longer replay the full transcript on every turn — only the new message + files.
const sendSkAiMessage = async (message, _history = [], files = [], _summary = "") => {
  try {
    const formData = new FormData();
    formData.append("message", message);
    files.forEach((f) => formData.append("files[]", f));
    const res = await https().post("/compliance/chat", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res?.data?.data || null;
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

// ── SkAi conversation memory (allocation / refresh / upgrade) ──
const getSkAiMemory = async () => {
  try {
    const res = await https().get("/compliance/skai-memory");
    return res?.data?.data || null;
  } catch (error) {
    return null;
  }
};

const refreshSkAiMemory = async () => {
  try {
    const res = await https().post("/compliance/skai-memory/refresh");
    return res?.data?.data || null;
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

const upgradeSkAiMemory = async (tier) => {
  try {
    const res = await https().post("/compliance/skai-memory/upgrade", { tier });
    return res?.data?.data || null;
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

// ── Staged writes (propose → confirm) ──
const commitSkAiAction = async (stagedActionId) => {
  try {
    const res = await https().post("/compliance/skai-commit", { stagedActionId });
    return res?.data?.data || null;
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

const cancelSkAiAction = async (stagedActionId) => {
  try {
    const res = await https().post("/compliance/skai-cancel", { stagedActionId });
    return res?.data?.data || null;
  } catch (error) {
    return null;
  }
};

// ── Proactive alerts (widget badge + tap-to-fix) ──
const getSkAiAlerts = async () => {
  try {
    const res = await https().get("/compliance/skai-alerts");
    return res?.data?.data || null;
  } catch (error) {
    return null;
  }
};

const fetchSkAiGreeting = async () => {
  try {
    const res = await https().get("/compliance/skai-greeting");
    return res?.data?.data || null;
  } catch (error) {
    return null;
  }
};

// ── Requirement Approval ──
const approveClientComplianceItem = async (id) => {
  try {
    const response = await https().put(`/compliance/client-items/${id}/approve`);
    if (response?.status === 200) {
      const updated = response.data?.item || response.data;
      store.dispatch(patchClientItem(updated));
      return updated;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

const approveFacilityComplianceItem = async (id) => {
  try {
    const response = await https().put(`/compliance/facility-items/${id}/approve`);
    if (response?.status === 200) {
      const updated = response.data?.item || response.data;
      store.dispatch(patchFacilityItem(updated));
      return updated;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
    return null;
  }
};

export {
  getComplianceDashboard,
  getComplianceAlerts,
  getCredentialTypes,
  addCredentialType,
  updateCredentialType,
  deleteCredentialType,
  getStaffCredentials,
  getStaffCredentialsOverview,
  saveStaffCredential,
  verifyStaffCredential,
  deleteStaffCredential,
  getClientComplianceItems,
  addClientComplianceItem,
  updateClientComplianceItem,
  deleteClientComplianceItem,
  getClientComplianceRecords,
  getClientComplianceOverview,
  saveClientComplianceRecord,
  getClientDocumentsOverview,
  getFacilityDocumentsOverview,
  getFacilityComplianceItems,
  addFacilityComplianceItem,
  updateFacilityComplianceItem,
  deleteFacilityComplianceItem,
  getFacilityComplianceRecords,
  saveFacilityComplianceRecord,
  runComplianceAudit,
  getComplianceSettings,
  updateComplianceSettings,
  getDocumentTemplates,
  toggleDocumentTemplate,
  toggleDocumentFolder,
  createCustomFolder,
  addCustomItem,
  deleteCustomFolder,
  deleteCustomItem,
  getOperationalTasks,
  createOperationalTask,
  updateOperationalTask,
  deleteOperationalTask,
  updateOperationalTaskStatus,
  getMyComplianceTasks,
  getOverdueStaffTasks,
  getTaskInstances,
  updateTaskInstanceStatus,
  triggerInstanceGeneration,
  getWeeklyTaskBoard,
  updateTaskInstanceStaffStatus,
  approveClientComplianceItem,
  approveFacilityComplianceItem,
  getStateRequirements,
  sendSkAiMessage,
  fetchSkAiGreeting,
  getSkAiMemory,
  refreshSkAiMemory,
  upgradeSkAiMemory,
  commitSkAiAction,
  cancelSkAiAction,
  getSkAiAlerts,
};
